import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { ApiClientError } from '../../services/apiClient'
import {
  adminClient,
  type AdminPlan,
  type AdminUserDetail,
  type AdminUserListItem,
} from '../../services/adminClient'
import { Modal } from '../../components/Modal'
import { ConfirmModal } from '../../components/ConfirmModal'

const PAGE_SIZE = 25
const SEARCH_DEBOUNCE_MS = 300

/**
 * Gestión de usuarios desde el portal admin (§12.5 SRS).
 *   - Lazy load 25 por carga.
 *   - Búsqueda tiempo real por email/nombre/apellidos.
 *   - Click en fila → modal con detalle + plan + count reportes + toggle activar/desactivar.
 */
export function AdminUsersPage() {
  const [items, setItems] = useState<AdminUserListItem[]>([])
  const [cursor, setCursor] = useState<number | null>(null)
  const [hasMore, setHasMore] = useState(true)
  const [total, setTotal] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')

  const [selected, setSelected] = useState<AdminUserDetail | null>(null)
  const [modalError, setModalError] = useState<string | null>(null)
  const [editingPlan, setEditingPlan] = useState(false)

  const sentinelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [search])

  const loadFirst = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await adminClient.users.list({ q: debounced || undefined, limit: PAGE_SIZE })
      setItems(res.items)
      setCursor(res.next_cursor)
      setHasMore(res.next_cursor !== null)
      setTotal(res.total)
    } catch (err) {
      setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo cargar.')
    } finally {
      setLoading(false)
    }
  }, [debounced])

  useEffect(() => { loadFirst() }, [loadFirst])

  useEffect(() => {
    if (!hasMore || cursor === null) return
    const node = sentinelRef.current
    if (!node) return
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !loading) loadMore(cursor)
    }, { rootMargin: '200px' })
    obs.observe(node)
    return () => obs.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor, hasMore, loading])

  async function loadMore(from: number) {
    setLoading(true)
    try {
      const res = await adminClient.users.list({ q: debounced || undefined, cursor: from, limit: PAGE_SIZE })
      setItems((prev) => [...prev, ...res.items])
      setCursor(res.next_cursor)
      setHasMore(res.next_cursor !== null)
    } catch (err) {
      setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo cargar más.')
    } finally {
      setLoading(false)
    }
  }

  async function openDetail(user: AdminUserListItem) {
    setModalError(null)
    try {
      const res = await adminClient.users.show(user.id)
      setSelected(res.user)
    } catch (err) {
      setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo cargar el usuario.')
    }
  }

  async function handleToggleActive() {
    if (!selected) return
    setModalError(null)
    try {
      const res = await adminClient.users.setActive(selected.id, !selected.active)
      setSelected(res.user)
      setItems((prev) => prev.map((u) => u.id === res.user.id ? { ...u, active: res.user.active } : u))
    } catch (err) {
      setModalError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo actualizar.')
    }
  }

  return (
    <div className="content-stack">
      <header className="list-toolbar">
        <div className="list-toolbar__title">
          <h1>Usuarios</h1>
          {total !== null && <span className="muted">{total.toLocaleString('es-MX')} usuarios</span>}
        </div>
        <div className="list-toolbar__actions">
          <input
            type="search"
            className="search-input"
            placeholder="Buscar por correo o nombre…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </header>

      {error && <p className="error">{error}</p>}

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Correo</th>
              <th>Tenant</th>
              <th>Estado</th>
              <th>Creado</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading && (
              <tr><td colSpan={5} className="data-table__empty">No hay usuarios.</td></tr>
            )}
            {items.map((u) => (
              <tr key={u.id} className="data-table__row" tabIndex={0}
                  onClick={() => openDetail(u)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDetail(u) } }}>
                <td><strong>{u.full_name}</strong></td>
                <td className="mono">{u.email}</td>
                <td>{u.tenant_id}</td>
                <td>
                  <span className={`badge ${u.active ? 'badge--pdf_generated' : 'badge--draft'}`}>
                    {u.active ? 'Activo' : 'Inactivo'}
                  </span>
                </td>
                <td className="mono">{shortDate(u.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading && <p className="muted center">Cargando…</p>}
        {hasMore && cursor !== null && <div ref={sentinelRef} aria-hidden="true" style={{ height: 1 }} />}
      </div>

      <Modal
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={selected ? selected.full_name : 'Usuario'}
        description={selected?.email}
        size="md"
        footer={selected && (
          <div className="modal__actions modal__actions--split">
            <button
              type="button"
              className={selected.active ? 'danger-btn' : 'primary-btn'}
              onClick={handleToggleActive}
            >
              {selected.active ? 'Desactivar cuenta' : 'Activar cuenta'}
            </button>
            <div className="modal__actions">
              <button type="button" className="ghost-btn" onClick={() => setEditingPlan(true)}>
                Editar plan
              </button>
              <button type="button" className="ghost-btn" onClick={() => setSelected(null)}>Cerrar</button>
            </div>
          </div>
        )}
      >
        {modalError && <p className="error" style={{ marginTop: 0 }}>{modalError}</p>}
        {selected && (
          <dl className="detail-dl">
            <dt>Estado</dt>
            <dd>
              <span className={`badge ${selected.active ? 'badge--pdf_generated' : 'badge--draft'}`}>
                {selected.active ? 'Activo' : 'Inactivo'}
              </span>
            </dd>

            <dt>Tenant</dt>
            <dd>{selected.tenant_id}</dd>

            <dt>Plan vigente</dt>
            <dd>
              {selected.plan
                ? <>{selected.plan.name} <small className="muted">· {selected.plan.billing_type} · {selected.plan.status}</small></>
                : <span className="muted">Sin suscripción</span>}
            </dd>

            {selected.plan && (selected.plan.monthly_price > 0 || selected.plan.yearly_price > 0) && (
              <>
                <dt>Vigencia</dt>
                <dd className="mono">
                  {formatDateDMY(selected.plan.start_date)} – {formatDateDMY(selected.plan.current_period_end)}
                </dd>
              </>
            )}

            <dt>Reportes generados</dt>
            <dd className="mono">{selected.reports_total.toLocaleString('es-MX')}</dd>

            <dt>Último ingreso</dt>
            <dd className="mono">{formatDateDMY(selected.last_login_at)}</dd>

            <dt>Creado</dt>
            <dd className="mono">{formatDateDMY(selected.created_at)}</dd>

            <dt>Intentos fallidos</dt>
            <dd>{selected.failed_login_count}</dd>

            {selected.locked_until && (
              <>
                <dt>Bloqueado hasta</dt>
                <dd className="error mono">{formatDateDMY(selected.locked_until)}</dd>
              </>
            )}
          </dl>
        )}
      </Modal>

      {selected && editingPlan && (
        <PlanEditModal
          user={selected}
          onClose={() => setEditingPlan(false)}
          onSaved={(user) => {
            setSelected(user)
            setEditingPlan(false)
          }}
        />
      )}
    </div>
  )
}

/**
 * Edición de plan + vigencia del tenant del usuario (§12.5 SRS).
 * Flujo: formulario → un solo modal de confirmación con el resumen del cambio → aplicar.
 */
function PlanEditModal({
  user, onClose, onSaved,
}: {
  user: AdminUserDetail
  onClose: () => void
  onSaved: (user: AdminUserDetail) => void
}) {
  const [plans, setPlans] = useState<AdminPlan[]>([])
  const [loadingPlans, setLoadingPlans] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [planId, setPlanId] = useState<number | ''>(user.plan?.id ?? '')
  const [startDate, setStartDate] = useState(toInputDate(user.plan?.start_date))
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState(toInputDate(user.plan?.current_period_end))

  const [confirming, setConfirming] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    adminClient.planes.list()
      .then((res) => {
        setPlans(res.items)
        if (planId === '' && res.items.length > 0) setPlanId(res.items[0].id)
      })
      .catch((err) => setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudieron cargar los planes.'))
      .finally(() => setLoadingPlans(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (planId === '' || !startDate || !currentPeriodEnd) return
    if (currentPeriodEnd < startDate) {
      setError('La vigencia debe terminar en o después de la fecha de inicio.')
      return
    }
    setConfirming(true)
  }

  async function handleConfirm() {
    if (planId === '') return
    setSaving(true)
    setError(null)
    try {
      const res = await adminClient.users.updatePlan(user.id, {
        plan_id: planId,
        start_date: startDate,
        current_period_end: currentPeriodEnd,
      })
      onSaved(res.user)
    } catch (err) {
      setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo guardar el cambio.')
      setConfirming(false)
    } finally {
      setSaving(false)
    }
  }

  const selectedPlan = plans.find((p) => p.id === planId) ?? null

  return (
    <Modal
      open
      onClose={() => !saving && onClose()}
      title={`Editar plan — ${user.full_name}`}
      size="sm"
      dismissible={!saving}
      footer={
        <div className="modal__actions">
          <button type="button" className="ghost-btn" onClick={onClose} disabled={saving}>Cancelar</button>
          <button type="submit" form="plan-edit-form" className="primary-btn" disabled={loadingPlans || saving}>
            Guardar cambios
          </button>
        </div>
      }
    >
      <form id="plan-edit-form" className="form-grid" onSubmit={handleSubmit} noValidate>
        <label className="field">
          <span>Plan</span>
          <select
            value={planId === '' ? '' : String(planId)}
            onChange={(e) => setPlanId(e.target.value === '' ? '' : Number(e.target.value))}
            disabled={loadingPlans}
            required
          >
            {plans.map((p) => (
              <option key={p.id} value={String(p.id)}>{p.name}</option>
            ))}
          </select>
        </label>

        <div className="field-row">
          <label className="field">
            <span>Inicio de vigencia</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              required
            />
          </label>
          <label className="field">
            <span>Fin de vigencia</span>
            <input
              type="date"
              value={currentPeriodEnd}
              onChange={(e) => setCurrentPeriodEnd(e.target.value)}
              required
            />
          </label>
        </div>

        {error && <p className="error">{error}</p>}
      </form>

      <ConfirmModal
        open={confirming}
        title="Confirmar cambio de plan"
        message={
          `¿Confirmas asignar el plan "${selectedPlan?.name ?? ''}" con vigencia del `
          + `${formatDateDMY(startDate)} al ${formatDateDMY(currentPeriodEnd)} para ${user.full_name}? `
          + 'Esta acción sustituye la suscripción actual del tenant de inmediato.'
        }
        confirmLabel="Sí, aplicar cambio"
        loading={saving}
        onConfirm={handleConfirm}
        onCancel={() => !saving && setConfirming(false)}
      />
    </Modal>
  )
}

/** Convierte una fecha ISO/"YYYY-MM-DD HH:mm:ss" a "YYYY-MM-DD" para <input type="date">. */
function toInputDate(value: string | null | undefined): string {
  if (!value) return ''
  return value.slice(0, 10)
}

function shortDate(value: string | null): string {
  if (!value) return '—'
  try {
    return new Date(value.replace(' ', 'T')).toLocaleDateString('es-MX', {
      year: 'numeric', month: 'short', day: '2-digit',
    })
  } catch {
    return value
  }
}

const MONTHS_ES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']

/** Formato "10-Jul-2026", usado en el modal de detalle de usuario. */
function formatDateDMY(value: string | null | undefined): string {
  if (!value) return '—'
  const d = new Date(value.replace(' ', 'T'))
  if (isNaN(d.getTime())) return value
  const day = String(d.getDate()).padStart(2, '0')
  return `${day}-${MONTHS_ES[d.getMonth()]}-${d.getFullYear()}`
}
